import { useEffect, useState } from "react";
import { supabase } from "./supabase";

// Hand-typed mirror of the migration in
// supabase/migrations/20260527100000_meta_ads_schema.sql. Replaced with
// generated `Database["public"]["Tables"]` types after the migration lands
// on main and `supabase gen types` is re-run.

export type MetaAccount = {
  id: string;
  name: string | null;
  account_status: number | null;
  currency: string | null;
  timezone_name: string | null;
  business_country_code: string | null;
  raw: unknown;
  synced_at: string;
};

export type MetaCampaign = {
  id: string;
  account_id: string;
  name: string;
  status: string;
  effective_status: string | null;
  objective: string | null;
  buying_type: string | null;
  start_time: string | null;
  stop_time: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  raw: unknown;
  synced_at: string;
};

export type MetaCampaignMetricsDaily = {
  campaign_id: string;
  account_id: string;
  date: string;
  impressions: number;
  reach: number;
  clicks: number;
  spend: number;
  cpc: number | null;
  cpm: number | null;
  ctr: number | null;
  frequency: number | null;
  conversions: number;
  conversions_value: number;
  raw: unknown;
  synced_at: string;
};

export type MetaSyncRun = {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: "running" | "ok" | "error";
  campaigns_upserted: number | null;
  metrics_upserted: number | null;
  error: string | null;
  raw: unknown;
};

export type MetaAdsData = {
  account: MetaAccount | null;
  campaigns: MetaCampaign[];
  metrics: MetaCampaignMetricsDaily[];
  syncRuns: MetaSyncRun[];
  loading: boolean;
  error: string | null;
};

const LOOKBACK_DAYS = 30;

export function useMetaAdsData(): MetaAdsData {
  const [account, setAccount] = useState<MetaAccount | null>(null);
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([]);
  const [metrics, setMetrics] = useState<MetaCampaignMetricsDaily[]>([]);
  const [syncRuns, setSyncRuns] = useState<MetaSyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const client = supabase();
    let cancelled = false;

    async function fetchAll() {
      try {
        const since = new Date();
        since.setDate(since.getDate() - LOOKBACK_DAYS);
        const sinceIso = since.toISOString().slice(0, 10);

        const [a, c, m, r] = await Promise.all([
          client.from("meta_ads_accounts").select("*").limit(1).maybeSingle(),
          client.from("meta_ads_campaigns").select("*").order("name"),
          client.from("meta_ads_campaign_metrics_daily").select("*").gte("date", sinceIso),
          client.from("meta_ads_sync_runs").select("*").order("started_at", { ascending: false }).limit(20),
        ]);

        if (cancelled) return;
        if (a.error && a.error.code !== "PGRST116") throw a.error;
        if (c.error) throw c.error;
        if (m.error) throw m.error;
        if (r.error) throw r.error;

        setAccount((a.data as MetaAccount) ?? null);
        setCampaigns((c.data as MetaCampaign[]) ?? []);
        setMetrics((m.data as MetaCampaignMetricsDaily[]) ?? []);
        setSyncRuns((r.data as MetaSyncRun[]) ?? []);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();

    const channel = client
      .channel("meta-ads-data")
      .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_accounts" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_campaigns" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_campaign_metrics_daily" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_sync_runs" }, fetchAll)
      .subscribe();

    return () => {
      cancelled = true;
      client.removeChannel(channel);
    };
  }, []);

  return { account, campaigns, metrics, syncRuns, loading, error };
}

export type MetaKpis = {
  impressions: number;
  reach: number;
  clicks: number;
  spend: number;
  conversions: number;
  conversionsValue: number;
  ctr: number;
  avgCpc: number;
};

export function aggregateMetaKpis(rows: MetaCampaignMetricsDaily[]): MetaKpis {
  let impressions = 0;
  let reach = 0;
  let clicks = 0;
  let spend = 0;
  let conversions = 0;
  let conversionsValue = 0;
  for (const r of rows) {
    impressions += r.impressions;
    reach = Math.max(reach, r.reach); // reach is deduplicated; sum doesn't make sense across days
    clicks += r.clicks;
    spend += Number(r.spend);
    conversions += Number(r.conversions);
    conversionsValue += Number(r.conversions_value);
  }
  return {
    impressions,
    reach,
    clicks,
    spend,
    conversions,
    conversionsValue,
    ctr: impressions > 0 ? clicks / impressions : 0,
    avgCpc: clicks > 0 ? spend / clicks : 0,
  };
}

export function aggregateMetaByCampaign(
  campaigns: MetaCampaign[],
  rows: MetaCampaignMetricsDaily[],
): Array<MetaCampaign & { kpis: MetaKpis }> {
  const byCampaign = new Map<string, MetaCampaignMetricsDaily[]>();
  for (const r of rows) {
    const list = byCampaign.get(r.campaign_id) ?? [];
    list.push(r);
    byCampaign.set(r.campaign_id, list);
  }
  return campaigns.map((c) => ({
    ...c,
    kpis: aggregateMetaKpis(byCampaign.get(c.id) ?? []),
  }));
}
